import { useState } from "react";
import { AlertTriangle, BrainCircuit, Check, ChevronDown, ChevronRight, CircleAlert, ExternalLink, FileText, FlaskConical, Leaf, Palette, Smile, Snowflake, Sparkles, User, Waves } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { READINESS } from "@/components/Badges";
import { Disclosure } from "@/components/Brand";
import { AuthImage } from "@/components/AuthImage";
import { PigmentBottle } from "@/components/kit/PigmentBottle";

const SOURCE_LABEL = { kit: "From your kit", kit_second: "Also in your kit", outside_kit: "Outside your kit" };
const LOVE = [["layers", "L", "Layers"], ["optics", "O", "Optics"], ["variables", "V", "Variables"], ["evaluation", "E", "Evaluation"]];
const CHIP_ICON = { skin: User, undertone: Snowflake, hair: Waves, technique: Sparkles, lips: Smile, goal: Palette, other: Leaf };
const CONF = { high: "High", moderate: "Moderate", limited: "Limited" };
const INGR = { verified_shade: "Documented", partial: "Partly documented", unverified: "Not verified", name_only: "Not verified" };

const splitItem = (t) => {
  const m = String(t).split(/\s+[—–-]\s+|:\s+/);
  return m.length > 1 ? [m[0], m.slice(1).join(" — ")] : [t, ""];
};

function Tile({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="flex min-w-[160px] flex-1 items-center gap-2.5 rounded-xl border border-[#EFE6DA] bg-[#FDFBF7] px-3 py-2.5" data-testid={testId}>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone}`}><Icon size={16} strokeWidth={1.8} /></span>
      <span className="min-w-0 leading-tight">
        <span className="block text-[11px] text-[#6E5549]">{label}</span>
        <span className="block text-sm font-semibold text-[#2C1810]">{value}</span>
      </span>
    </div>
  );
}

function Row({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3.5 border-t border-[#EFE6DA] py-4">
      <Icon size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C1810]" />
      <div className="min-w-0">
        <p className="text-[15px] font-medium text-[#2C1810]">{title}</p>
        <div className="mt-0.5 text-sm leading-relaxed text-[#6E5549]">{children}</div>
      </div>
    </div>
  );
}

function ChecklistDialog({ open, onOpenChange, title, items, testId }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-[#FDFBF7]" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-serif text-3xl">{title}</DialogTitle></DialogHeader>
        <ChecklistItems items={items} testId={`${testId}-items`} />
      </DialogContent>
    </Dialog>
  );
}

function ChecklistItems({ items, testId }) {
  const [done, setDone] = useState({});
  return (
    <ul className="space-y-4" data-testid={testId}>
      {items.map((c, j) => {
        const [t, d] = splitItem(c);
        return (
          <li key={j}>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox data-testid={`${testId}-${j}`} checked={!!done[j]} onCheckedChange={(v) => setDone({ ...done, [j]: v })}
                className="mt-0.5 h-5 w-5 rounded-md border-[#2C1810] data-[state=checked]:bg-[#2C1810]" />
              <span className={done[j] ? "opacity-50" : ""}>
                <span className="block text-[15px] font-medium text-[#2C1810]">{t}</span>
                {d && <span className="mt-0.5 block text-sm leading-relaxed text-[#6E5549]">{d}</span>}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

function OptionCard({ o, i, mid }) {
  const [open, setOpen] = useState(false);
  const p = `rec-${mid}-option-${i}`;
  const verified = o.verification_status === "verified_shade" || o.verification_status === "partial";
  const limited = !o.confidence || o.confidence === "limited";
  const checklist = o.verification_checklist?.length ? o.verification_checklist : ["Check bottle label — Confirm the brand, line and shade on your current bottle.", "Confirm CI numbers — Match the ingredient list with your bottle and lot."];
  return (
    <div data-testid={p} className="ig-rise flex min-w-0 flex-col rounded-3xl border border-[#EFE6DA] bg-white p-5 shadow-[0_12px_40px_rgba(44,24,16,0.05)] sm:p-6" style={{ animationDelay: `${i * 0.08}s` }}>
      <div className="flex gap-4">
        {o.image_file_id
          ? <AuthImage id={o.image_file_id} alt={`${o.brand || ""} ${o.shade || ""} bottle`} className="h-32 w-20 shrink-0 rounded-2xl object-cover sm:h-40 sm:w-28" testId={`${p}-photo`} />
          : <div className="w-20 shrink-0 sm:w-24"><PigmentBottle item={{ brand: o.brand, line: o.line, shade: o.shade || o.title }} compact /></div>}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6E5549]">{SOURCE_LABEL[o.source] || `Option ${i + 1}`}</p>
          <h4 lang="en" className="mt-1.5 break-words font-serif text-lg font-semibold leading-tight text-[#2C1810] hyphens-auto sm:text-xl xl:text-2xl" data-testid={`${p}-shade`}>{o.shade || o.title}</h4>
          <p className="mt-1 text-sm text-[#6E5549]">{[o.brand, o.line].filter(Boolean).join(" · ")}</p>
          {o.title && o.title !== o.shade && <p className="mt-2 text-sm italic text-[#4A3B32]" data-testid={`${p}-title`}>{o.title}</p>}
          {o.formulation_category && <p className="mt-1 text-xs text-[#9C6218]" data-testid={`${p}-formulation`}>{o.formulation_category}</p>}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Tile icon={limited ? AlertTriangle : Check} label="Confidence" value={CONF[o.confidence] || "Limited"} testId={`${p}-confidence`}
          tone={limited ? "bg-[#FAF2E6] text-[#9C6218]" : "bg-[#EAF3ED] text-[#2E6B4F]"} />
        <Tile icon={FileText} label="Ingredients" value={INGR[o.verification_status] || "Not verified"} testId={`${p}-verification`} tone="bg-[#F6F1EA] text-[#2C1810]" />
      </div>
      {o.verification_note && !verified && <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-[#9C6218]" data-testid={`${p}-note`}>{o.verification_note}</p>}
      <div className="mt-4 flex-1">
        <Row icon={Leaf} title="Why it may fit">{o.why_it_fits}</Row>
        <Row icon={CircleAlert} title="Artist considerations">{o.tradeoffs}</Row>
        <Row icon={FlaskConical} title="Ingredient evidence">
          {o.ci_numbers?.length ? (
            <>
              <span className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`${p}-ci`}>
                {o.ci_numbers.map((c) => <span key={c} className="rounded-full border border-[#E6DBCF] bg-[#FDFBF7] px-3 py-1 text-xs text-[#2C1810]">CI {String(c).replace(/^CI\s*/i, "")}</span>)}
              </span>
              <span className="mt-2 block">Verify against your current bottle.</span>
            </>
          ) : "CI numbers not verified."}
        </Row>
      </div>
      <button onClick={() => setOpen(true)} data-testid={`${p}-verify-btn`}
        className={`mt-2 inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors ${verified ? "bg-[#B08A4E] text-white hover:bg-[#9c7a43]" : "border border-[#2C1810] text-[#2C1810] hover:bg-[#F6F1EA]"}`}>
        {verified ? "Review verification" : "Verify bottle ingredients"} <ChevronRight size={16} />
      </button>
      {o.source_url && (
        <a href={o.source_url} target="_blank" rel="noreferrer" data-testid={`${p}-source-link`} className="mt-3 inline-flex items-center gap-2 self-start text-sm text-[#2C1810] underline underline-offset-4">
          <ExternalLink size={15} /> View source
        </a>
      )}
      <ChecklistDialog open={open} onOpenChange={setOpen} title={`Verify ${o.shade || o.title || "this pigment"}`} items={checklist} testId={`${p}-checklist`} />
    </div>
  );
}

function BeforeYouProceed({ items, mid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-3xl border border-[#EFE6DA] bg-[#FDFBF7] p-5 sm:p-6" data-testid={`rec-${mid}-decision-checklist`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h4 className="font-serif text-3xl font-semibold text-[#2C1810]">Before you proceed</h4>
          <p className="mt-1 text-sm text-[#6E5549]">Complete the following to move forward with your chosen option.</p>
        </div>
        <div className="flex items-start gap-3 rounded-2xl bg-[#F6F1EA] p-4 lg:max-w-sm">
          <FileText size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#2C1810]" />
          <div>
            <p className="text-[15px] font-medium text-[#2C1810]">Current bottle: not checked</p>
            <p className="text-sm text-[#6E5549]">Verify the ingredient list for the exact bottle you are using.</p>
          </div>
        </div>
      </div>
      <div className="mt-6"><ChecklistItemsGrid items={items.slice(0, 6)} mid={mid} /></div>
      <button onClick={() => setOpen(true)} data-testid={`rec-${mid}-open-checklist`}
        className="mt-6 inline-flex min-h-[50px] w-full items-center justify-center gap-3 rounded-xl bg-[#1E1410] text-sm font-semibold text-[#FDFBF7] hover:bg-[#2C1810] sm:w-auto sm:px-10">
        <FileText size={17} /> Open checklist <ChevronRight size={16} />
      </button>
      <ChecklistDialog open={open} onOpenChange={setOpen} title="Before you proceed" items={items} testId={`rec-${mid}-full-checklist`} />
    </div>
  );
}

function ChecklistItemsGrid({ items, mid }) {
  const [done, setDone] = useState({});
  return (
    <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((c, j) => {
        const [t, d] = splitItem(c);
        return (
          <li key={j}>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox data-testid={`rec-${mid}-decision-item-${j}`} checked={!!done[j]} onCheckedChange={(v) => setDone({ ...done, [j]: v })}
                className="mt-0.5 h-5 w-5 rounded-md border-[#2C1810] data-[state=checked]:bg-[#2C1810]" />
              <span className={done[j] ? "opacity-50" : ""}>
                <span className="block text-[15px] font-medium text-[#2C1810]">{t}</span>
                {d && <span className="mt-0.5 block text-sm leading-relaxed text-[#6E5549]">{d}</span>}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

export function RecommendationView({ rec, mid, hideDisclosure = false }) {
  const r = READINESS[rec.readiness?.status] || READINESS.more_info;
  const chips = (rec.case_summary || []).filter((c) => c?.text).slice(0, 6);
  const love = rec.love_summary;
  return (
    <div className="space-y-5" data-testid={`rec-${mid}`}>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]">Consultation complete</p>
        <h3 className="mt-2 font-serif text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">Your pigment comparison</h3>
        <p className="mt-2 text-base text-[#6E5549]">Based on your client details, here are pigment options to consider.</p>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2.5" data-testid={`rec-${mid}-case-chips`}>
          {chips.map((c, j) => {
            const Icon = CHIP_ICON[c.icon] || Leaf;
            return <span key={j} className="inline-flex min-h-[44px] items-center gap-2.5 rounded-full border border-[#EFE6DA] bg-[#FDFBF7] px-5 text-sm text-[#2C1810]"><Icon size={17} strokeWidth={1.5} />{c.text}</span>;
          })}
        </div>
      )}
      <div className={`rounded-2xl border px-5 py-4 ${r.cls}`} data-testid={`rec-${mid}-readiness`}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-80">Readiness · <span className="font-serif text-base normal-case tracking-normal">{r.label}</span></p>
        {rec.readiness?.rationale && <p className="mt-1 text-sm leading-relaxed text-[#4A3B32]">{rec.readiness.rationale}</p>}
      </div>
      {rec.options?.length > 0 && <div className="grid gap-4 md:grid-cols-2">{rec.options.map((o, i) => <OptionCard key={`${o.brand}-${o.line}-${o.shade || o.title}-${i}`} o={o} i={i} mid={mid} />)}</div>}
      {rec.gaps?.length > 0 && (
        <div className="rounded-2xl border border-dashed border-[#D99B90] bg-[#FDF2F0] p-4" data-testid={`rec-${mid}-gaps`}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9C6218]">Why no further options</p>
          <ul className="mt-1 space-y-1 text-sm text-[#4A3B32]">{rec.gaps.map((g, j) => <li key={j}>{g}</li>)}</ul>
        </div>
      )}
      {rec.decision_checklist?.length > 0 && <BeforeYouProceed items={rec.decision_checklist} mid={mid} />}
      {love && (
        <Collapsible className="rounded-3xl border border-[#EFE6DA] bg-white">
          <CollapsibleTrigger data-testid={`rec-${mid}-love-summary-toggle`} className="group flex w-full items-center gap-4 px-6 py-5 text-left">
            <BrainCircuit size={30} strokeWidth={1.3} className="shrink-0 text-[#2C1810]" />
            <span className="flex-1">
              <span className="block font-serif text-xl font-semibold text-[#2C1810]">Explore L.O.V.E. reasoning</span>
              <span className="block text-sm text-[#6E5549]">View the reasoning behind these options.</span>
            </span>
            <ChevronDown size={18} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-6 pb-6">
            <div className="grid gap-3 sm:grid-cols-2" data-testid={`rec-${mid}-love-summary`}>
              {LOVE.map(([k, letter, name]) => love[k] ? (
                <div key={k} className="rounded-xl bg-[#FDF2F0] p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9C6218]"><span className="font-serif text-lg italic text-[#2C1810]">{letter}</span> · {name}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#4A3B32]">{love[k]}</p>
                </div>
              ) : null)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      {!hideDisclosure && <Disclosure compact testId={`rec-${mid}-disclosure`} />}
      <p className="pb-2 text-right text-[11px] tracking-wide text-[#A88B7D]">Drawn bottles are illustrative; photos are uploaded by the owner or you · always confirm your actual bottle</p>
    </div>
  );
}
